import { ClockIcon, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";

import { PlaylistOptions } from "@/app/components/playlist/options";
import { TableActionButton } from "@/app/components/table/action-button";
import { CoverImage } from "@/app/components/table/cover-image";
import PlaySongButton from "@/app/components/table/play-button";
import { DataTableColumnHeader } from "@/app/components/ui/data-table-column-header";
import { SimpleTooltip } from "@/app/components/ui/simple-tooltip";
import {
  columnProps,
  getLayoutMap,
  playlistColumnLayouts,
} from "@/app/tables/column-layouts";
import i18n from "@/i18n";
import { ROUTES } from "@/routes/routesList";
import { ColumnDefType } from "@/types/react-table/columnDef";
import { Playlist } from "@/types/responses/playlist";
import { convertSecondsToTime } from "@/utils/convertSecondsToTime";

export function playlistsColumns(): ColumnDefType<Playlist>[] {
  const layouts = getLayoutMap(playlistColumnLayouts);

  return [
    {
      id: "index",
      accessorKey: "index",
      ...columnProps(layouts.index),
      header: () => {
        return <div className="w-full text-center">#</div>;
      },
      cell: ({ row }) => {
        const index = row.index + 1;
        const playlist = row.original;

        return <PlaySongButton trackNumber={index} trackId={playlist.id} />;
      },
    },
    {
      id: "name",
      accessorKey: "name",
      enableSorting: true,
      sortingFn: "customSortFn",
      ...columnProps(layouts.name),
      header: ({ column, table }) => (
        <DataTableColumnHeader column={column} table={table}>
          {i18n.t("table.columns.name")}
        </DataTableColumnHeader>
      ),
      cell: ({ row }) => (
        <div className="flex gap-2 items-center w-full">
          <CoverImage
            coverArt={row.original.coverArt}
            coverArtType="playlist"
            altText={row.original.name}
          />
          <div className="flex flex-col max-w-full justify-center truncate">
            <Link
              to={ROUTES.PLAYLIST.PAGE(row.original.id)}
              className="hover:underline truncate"
            >
              {row.original.name}
            </Link>
          </div>
        </div>
      ),
    },
    {
      id: "comment",
      accessorKey: "comment",
      ...columnProps(layouts.comment),
      header: i18n.t("table.columns.comment"),
      cell: ({ row }) => (
        <div className="text-muted-foreground w-full truncate">
          <p className="truncate">{row.original.comment}</p>
        </div>
      ),
    },
    {
      id: "songCount",
      accessorKey: "songCount",
      enableSorting: true,
      sortingFn: "basic",
      ...columnProps(layouts.songCount),
      header: ({ column, table }) => (
        <DataTableColumnHeader column={column} table={table}>
          {i18n.t("table.columns.songCount")}
        </DataTableColumnHeader>
      ),
    },
    {
      id: "duration",
      accessorKey: "duration",
      ...columnProps(layouts.duration),
      header: () => (
        <SimpleTooltip text={i18n.t("table.columns.duration")}>
          <ClockIcon className="w-4 h-4" />
        </SimpleTooltip>
      ),
      cell: ({ row }) => {
        const { duration } = row.original;
        const formattedDuration = convertSecondsToTime(duration ?? 0);

        return formattedDuration;
      },
    },
    {
      id: "public",
      accessorKey: "public",
      ...columnProps(layouts.public),
      header: i18n.t("table.columns.public"),
      cell: ({ row }) => (
        <div>
          {row.original.public ? (
            <Eye className="w-5 h-5 text-white-500" />
          ) : (
            <EyeOff className="w-5 h-5 text-gray-500" />
          )}
        </div>
      ),
    },
    {
      id: "actions",
      accessorKey: "actions",
      ...columnProps(layouts.actions),
      header: "",
      cell: ({ row }) => {
        const playlist = row.original;
        const disableOption = playlist.songCount === 0;

        return (
          <>
            <TableActionButton
              optionsMenuItems={
                <PlaylistOptions
                  playlist={playlist}
                  disablePlayNext={disableOption}
                  disableAddLast={disableOption}
                />
              }
            />
          </>
        );
      },
    },
  ];
}
